'use client';

import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import {
  MARKETING_PAIR_BLOCK_SX,
  MarketingNumberedBlock,
} from '@/components/website/Marketing/MarketingIndexChrome';
import { MARKETING_BODY_SX } from '@/components/website/Marketing/marketing-tokens';
import {
  ProcessDetailList,
  ProcessPairCopy,
  ProcessPairImageGrid,
  ProcessPairStage,
  ProcessTimelinePhase,
  PROCESS_APPROVALS_STAGE_PAPER_SX,
  PROCESS_PAIR_STAGE_SINGLE_IMAGE_SX,
  PROCESS_PRODUCTION_WARNING_SX,
  PROCESS_SUBHEAD_SX,
} from '@/components/website/OurProcess/process-section-ui';
import MarketingSectionImage from './MarketingSectionImage';
import { resolvedImageAlt, resolvedImageUrl, sectionImageValue } from './marketing-page-utils';

function containObjectFit(value) {
  return value === 'contain' ? { objectFit: 'contain' } : {};
}

function resolveGalleryImages(images) {
  return (images || [])
    .map((item, index) => {
      const src = resolvedImageUrl(item?.imageAssetId ?? item?.image ?? item);
      if (!src) return null;
      return {
        src,
        alt: item?.alt || resolvedImageAlt(item?.imageAssetId ?? item?.image, `Image ${index + 1}`),
        ...containObjectFit(item?.objectFit),
      };
    })
    .filter(Boolean);
}

export function ProcessTextImageSection({ section }) {
  const data = section.data || {};
  const image = sectionImageValue(data);
  const imageSrc = resolvedImageUrl(image);
  const imageAlt = resolvedImageAlt(image, data.heading || '');
  const items = (data.items || []).filter(item => item?.title || item?.body);

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      contentSx={MARKETING_PAIR_BLOCK_SX}
      stagger
    >
      <ProcessPairCopy
        eyebrow={data.eyebrow}
        heading={data.heading || ''}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        {...containObjectFit(data.objectFit)}
      >
        {(data.paragraphs || []).map((paragraph, index) =>
          paragraph ? (
            <Typography key={`${section.id}-p-${index}`} sx={MARKETING_BODY_SX}>
              {paragraph}
            </Typography>
          ) : null,
        )}
        {data.listHeading ? (
          <Typography sx={PROCESS_SUBHEAD_SX}>{data.listHeading}</Typography>
        ) : null}
        {items.length ? <ProcessDetailList items={items} /> : null}
      </ProcessPairCopy>
    </MarketingNumberedBlock>
  );
}

ProcessTextImageSection.propTypes = { section: PropTypes.object.isRequired };

export function ProcessBuildSection({ section }) {
  const data = section.data || {};
  const image = sectionImageValue(data);
  const imageSrc = resolvedImageUrl(image);
  const imageAlt = resolvedImageAlt(image, data.heading || '');
  const stages = (data.stages || []).filter(item => item?.title || item?.body);

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      contentSx={MARKETING_PAIR_BLOCK_SX}
      stagger
    >
      <ProcessPairCopy
        eyebrow={data.eyebrow}
        heading={data.heading || ''}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        {...containObjectFit(data.objectFit)}
      >
        {(data.paragraphs || []).map((paragraph, index) =>
          paragraph ? (
            <Typography key={`${section.id}-p-${index}`} sx={MARKETING_BODY_SX}>
              {paragraph}
            </Typography>
          ) : null,
        )}
        {stages.length ? <ProcessDetailList items={stages} /> : null}
      </ProcessPairCopy>
    </MarketingNumberedBlock>
  );
}

ProcessBuildSection.propTypes = { section: PropTypes.object.isRequired };

export function ProcessApprovalsSection({ section }) {
  const data = section.data || {};
  const image = sectionImageValue(data);
  const imageSrc = resolvedImageUrl(image);
  const imageAlt = resolvedImageAlt(image, data.heading || '');
  const design = data.design || {};
  const production = data.production || {};
  const finish = data.finish || {};
  const installation = data.installation || {};
  const designImages = resolveGalleryImages(design.images);
  const productionImages = resolveGalleryImages(production.images);
  const finishImage = sectionImageValue(finish);
  const finishSrc = resolvedImageUrl(finishImage);
  const finishAlt = resolvedImageAlt(finishImage, finish.title || 'Green finish');
  const installImage = sectionImageValue(installation);
  const installSrc = resolvedImageUrl(installImage);
  const installAlt = resolvedImageAlt(installImage, installation.title || 'Installation');
  const checklist = (design.checklist || []).filter(item => item?.title || item?.body);

  return (
    <MarketingNumberedBlock id={data.indexId || section.id} num={data.indexNum || ''} stagger>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '40px', md: '56px' },
        }}
      >
        <Box sx={MARKETING_PAIR_BLOCK_SX}>
          <ProcessPairCopy
            eyebrow={data.eyebrow}
            heading={data.heading || ''}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            {...containObjectFit(data.objectFit)}
          >
            {data.intro ? <Typography sx={MARKETING_BODY_SX}>{data.intro}</Typography> : null}
          </ProcessPairCopy>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '20px', md: '24px' },
          }}
        >
          {design.title || checklist.length || designImages.length ? (
            <Paper elevation={0} sx={PROCESS_APPROVALS_STAGE_PAPER_SX}>
              <ProcessPairStage
                copy={
                  <ProcessTimelinePhase title={design.title || ''}>
                    {checklist.length ? <ProcessDetailList items={checklist} /> : null}
                  </ProcessTimelinePhase>
                }
                media={designImages.length ? <ProcessPairImageGrid images={designImages} /> : null}
              />
            </Paper>
          ) : null}

          {production.title ||
          production.intro ||
          (production.steps || []).length ||
          (production.warnings || []).length ||
          productionImages.length ? (
            <Paper elevation={0} sx={PROCESS_APPROVALS_STAGE_PAPER_SX}>
              <ProcessPairStage
                copy={
                  <ProcessTimelinePhase title={production.title || ''}>
                    {production.intro ? (
                      <Typography sx={MARKETING_BODY_SX}>{production.intro}</Typography>
                    ) : null}
                    {(production.steps || []).filter(Boolean).length ? (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: '1.25em',
                          listStyleType: 'disc',
                          listStylePosition: 'outside',
                        }}
                      >
                        {(production.steps || []).filter(Boolean).map((step, index) => (
                          <Typography
                            key={`${section.id}-step-${index}`}
                            component="li"
                            sx={{
                              ...MARKETING_BODY_SX,
                              maxWidth: 'none',
                              display: 'list-item',
                              mb: '8px',
                              '&:last-of-type': { mb: 0 },
                            }}
                          >
                            {step}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                    {(production.warnings || []).filter(Boolean).map((paragraph, index) => (
                      <Typography
                        key={`${section.id}-warn-${index}`}
                        sx={PROCESS_PRODUCTION_WARNING_SX}
                      >
                        {paragraph}
                      </Typography>
                    ))}
                  </ProcessTimelinePhase>
                }
                media={
                  productionImages.length ? (
                    <ProcessPairImageGrid images={productionImages} />
                  ) : null
                }
              />
            </Paper>
          ) : null}

          {finish.title || (finish.paragraphs || []).length || finishSrc ? (
            <Paper elevation={0} sx={PROCESS_APPROVALS_STAGE_PAPER_SX}>
              <ProcessPairStage
                copy={
                  <ProcessTimelinePhase title={finish.title || ''} eyebrow={finish.eyebrow || ''}>
                    {(finish.paragraphs || []).filter(Boolean).map((paragraph, index) => (
                      <Typography key={`${section.id}-finish-${index}`} sx={MARKETING_BODY_SX}>
                        {paragraph}
                      </Typography>
                    ))}
                  </ProcessTimelinePhase>
                }
                media={
                  finishSrc ? (
                    <MarketingSectionImage
                      src={finishSrc}
                      alt={finishAlt}
                      sizes="(max-width: 899px) 100vw, 50vw"
                      sx={PROCESS_PAIR_STAGE_SINGLE_IMAGE_SX}
                      {...containObjectFit(finish.objectFit)}
                    />
                  ) : null
                }
              />
            </Paper>
          ) : null}

          {installation.title || (installation.paragraphs || []).length || installSrc ? (
            <Paper elevation={0} sx={PROCESS_APPROVALS_STAGE_PAPER_SX}>
              <ProcessPairStage
                copy={
                  <ProcessTimelinePhase title={installation.title || ''}>
                    {(installation.paragraphs || []).filter(Boolean).map((paragraph, index) => (
                      <Typography key={`${section.id}-inst-${index}`} sx={MARKETING_BODY_SX}>
                        {paragraph}
                      </Typography>
                    ))}
                  </ProcessTimelinePhase>
                }
                media={
                  installSrc ? (
                    <MarketingSectionImage
                      src={installSrc}
                      alt={installAlt}
                      sizes="(max-width: 899px) 100vw, 50vw"
                      sx={PROCESS_PAIR_STAGE_SINGLE_IMAGE_SX}
                      {...containObjectFit(installation.objectFit)}
                    />
                  ) : null
                }
              />
            </Paper>
          ) : null}
        </Box>
      </Box>
    </MarketingNumberedBlock>
  );
}

ProcessApprovalsSection.propTypes = { section: PropTypes.object.isRequired };

export function ProcessUniqueSection({ section }) {
  const data = section.data || {};
  const image = sectionImageValue(data);
  const imageSrc = resolvedImageUrl(image);
  const imageAlt = resolvedImageAlt(image, data.heading || '');

  return (
    <MarketingNumberedBlock
      id={data.indexId || section.id}
      num={data.indexNum || ''}
      contentSx={MARKETING_PAIR_BLOCK_SX}
      stagger
    >
      <ProcessPairCopy
        eyebrow={data.eyebrow}
        heading={data.heading || ''}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        {...containObjectFit(data.objectFit)}
      >
        {(data.paragraphs || []).map((paragraph, index) =>
          paragraph ? (
            <Typography key={`${section.id}-p-${index}`} sx={MARKETING_BODY_SX}>
              {paragraph}
            </Typography>
          ) : null,
        )}
      </ProcessPairCopy>
    </MarketingNumberedBlock>
  );
}

ProcessUniqueSection.propTypes = { section: PropTypes.object.isRequired };
